import { type AnyAction, combineReducers } from 'redux';
import Immutable from 'immutable';
import { AspectRatio } from '@lumen5/luminary';

import multiVideosSlice, { setVideoName } from './multi-videos-slice';
import textAssetsReducer from './text-assets-slice';

import type {
    CreationSourceEnum,
    DashboardFlowEnum,
    LanguageEnum,
    VideoApprovalStatusEnum,
} from 'js/src/client';
import { VideoVisibility } from 'js/src/client';
import {
    markSceneAsFinishedLoading,
    markSceneAsLoading,
    markSceneGroupAsFinishedLoading,
    markSceneGroupAsLoading,
    markSceneGroupAsSkipped,
    setAdvancedMediaControlsState,
    setAsyncGenerationData,
    setAsyncGenerationTaskId,
    setAutoCreateSceneGroupFromUploadedAssetUuid,
    setBlueprintId,
    setChartSettingsState,
    setCreateVideoFlowAnalyticsMethod,
    setCreateVideoFlowAnalyticsThemeId,
    setDashboardFlow,
    setEditorTabsVisibility,
    setInitialBlueprintId,
    setIsAnimationVideo,
    setIsEditingDraft,
    setIsStoryboardGenerating,
    setLoadingCreatorActivityId,
    setParentVideoLanguage,
    setPendingChart,
    setProjectId,
    setRecommendedBlueprint,
    setSceneHandoffActive,
    setScriptComposerPlaceholderVideoId,
    setSelectedAspectRatio,
    setSelectedBrandKitId,
    setSelectedPaletteIndex,
    setSelectedTemplateId,
    setSharePageUrl,
    setSkipMediaOptimization,
    setSaveConflict,
    setStage,
    setTextOnMediaGenerating,
    setToolboxCreatingVideo,
    setToolboxTurnActive,
    latchToolboxSidebar,
    setTransitionSettingsState,
    setVideoApprovalStatus,
    setVideoAssignee,
    setVideoCreationSource,
    setVideoDataHash,
    setVideoFeedbackFormState,
    setVideoId,
    setVideoSlug,
    setVideoInitType,
    setVideoLanguage,
    setVideoOwnerId,
    setVideoProject,
    setVideoVisibility,
    setWindowWidth,
    switchEditorTabAction,
} from 'js/src/data/app/app-actions';
import type {
    AsyncGenerationData,
    PendingChart,
    RecommendedBlueprint,
    SCENE_GROUP_LOADING_TYPES,
    SceneGroupLoadingContextType,
} from 'js/src/data/app/app-types';
import { EDITOR_TAB_OPTIONS, STAGES, VIDEO_INIT_TYPE } from 'js/src/data/app/app-types';
import type { ImmutableMap, NEW_VIDEO_FLOW_CREATION_METHOD } from 'js/src/libs/consts';
import { ImmutableFromJS } from 'js/src/libs/consts';

export function editorTabsVisible(state = true, action: AnyAction) {
    if (setEditorTabsVisibility.match(action)) {
        return action.payload;
    }
    return state;
}

export function editor_tab(state = EDITOR_TAB_OPTIONS.MEDIA, action: AnyAction) {
    if (switchEditorTabAction.match(action)) {
        return action.payload.nextTab;
    }
    return state;
}

export function selected_aspect_ratio(state = AspectRatio.LANDSCAPE, action: AnyAction) {
    if (setSelectedAspectRatio.match(action)) {
        return action.payload.aspectRatio;
    }
    return state;
}

export function selected_template_id(state: string | null = null, action: AnyAction) {
    if (setSelectedTemplateId.match(action)) {
        return action.payload.templateId;
    }
    return state;
}

export function selected_palette_index(state: number | null = null, action: AnyAction) {
    if (setSelectedPaletteIndex.match(action)) {
        return action.payload.paletteIndex;
    }
    return state;
}

export function stage(state = STAGES.INIT, action: AnyAction) {
    if (setStage.match(action)) {
        return action.payload.stage;
    }
    return state;
}

export function video_id(state: number | null = null, action: AnyAction) {
    if (setVideoId.match(action)) {
        return action.payload.videoId;
    }
    return state;
}

export function video_slug(state: string | null = null, action: AnyAction) {
    if (setVideoSlug.match(action)) {
        return action.payload.videoSlug;
    }
    return state;
}

export function toolboxCreatingVideo(state = false, action: AnyAction) {
    if (setToolboxCreatingVideo.match(action)) {
        return action.payload;
    }
    return state;
}

export function toolboxTurnActive(state = false, action: AnyAction) {
    if (setToolboxTurnActive.match(action)) {
        return action.payload;
    }
    return state;
}

export function toolboxSidebarLatched(state = false, action: AnyAction) {
    if (latchToolboxSidebar.match(action)) {
        return true;
    }
    if (setToolboxTurnActive.match(action) && !action.payload) {
        return false;
    }
    return state;
}

export function scriptComposerPlaceholderVideoId(state: number | null = null, action: AnyAction) {
    if (setScriptComposerPlaceholderVideoId.match(action)) {
        return action.payload;
    }
    return state;
}

export function sceneHandoffActive(state = false, action: AnyAction) {
    if (setSceneHandoffActive.match(action)) {
        return action.payload;
    }
    return state;
}

export function videoCreationSource(state: CreationSourceEnum | null = null, action: AnyAction) {
    if (setVideoCreationSource.match(action)) {
        return action.payload.creationSource;
    }
    return state;
}

export function isAnimationVideo(state = false, action: AnyAction) {
    if (setIsAnimationVideo.match(action)) {
        return action.payload.isAnimationVideo;
    }
    return state;
}

export function parentVideoLanguage(state: string | null = null, action: AnyAction) {
    if (setParentVideoLanguage.match(action)) {
        return action.payload.language;
    }
    return state;
}

export function videoDataHash(state: string | null = null, action: AnyAction) {
    if (setVideoDataHash.match(action)) {
        return action.payload.videoDataHash;
    }
    return state;
}

export function saveConflict(state: boolean = false, action: AnyAction) {
    if (setSaveConflict.match(action)) {
        return action.payload;
    }
    return state;
}

export function sharePageUrl(state: string | null = null, action: AnyAction) {
    if (setSharePageUrl.match(action)) {
        return action.payload.sharePageUrl;
    }
    return state;
}

export function videoVisibility(state = VideoVisibility.UNLISTED, action: AnyAction) {
    if (setVideoVisibility.match(action)) {
        return action.payload.videoVisibility;
    }
    return state;
}

export function videoName(state: string | null = null, action: AnyAction) {
    if (setVideoName.match(action)) {
        return action.payload.videoName;
    }
    return state;
}

export function video_language(state: LanguageEnum | null = null, action: AnyAction) {
    if (setVideoLanguage.match(action)) {
        return action.payload.video_language;
    }
    return state;
}

export function video_project(state: number | null = null, action: AnyAction) {
    if (setVideoProject.match(action)) {
        return action.payload.projectId;
    }
    return state;
}

export function video_owner(state: number | null = null, action: AnyAction) {
    if (setVideoOwnerId.match(action)) {
        return action.payload.videoOwnerId;
    }
    return state;
}

/**
 * Maps scene group ids to an object of custom data describing the loading context
 */
type LoadingSceneGroupData = {
    type: SCENE_GROUP_LOADING_TYPES;
    context?: SceneGroupLoadingContextType;
    skipped?: boolean;
};
export function loadingSceneGroups(
    state = Immutable.Map<string, ImmutableMap<LoadingSceneGroupData>>(),
    action: AnyAction,
) {
    if (markSceneGroupAsLoading.match(action)) {
        return state.set(
            action.payload.sceneGroupId,
            ImmutableFromJS<LoadingSceneGroupData>({
                type: action.payload.type,
                context: action.payload.context,
            }),
        );
    }
    if (markSceneGroupAsFinishedLoading.match(action)) {
        return state.delete(action.payload.sceneGroupId);
    }
    if (markSceneGroupAsSkipped.match(action)) {
        const existingData = state.get(action.payload.sceneGroupId);
        if (existingData) {
            return state.set(action.payload.sceneGroupId, existingData.set('skipped', true));
        }
        return state;
    }
    return state;
}

/**
 * Maps scene ids to an object of custom data describing the loading context. This is the
 * scene-level counterpart to `loadingSceneGroups`: use it when only a single scene should show a
 * loading state (e.g. generating a chart on one scene) rather than the whole scene group.
 */
type LoadingSceneData = {
    type: SCENE_GROUP_LOADING_TYPES;
    context?: SceneGroupLoadingContextType;
};
export function loadingScenes(
    state = Immutable.Map<string, ImmutableMap<LoadingSceneData>>(),
    action: AnyAction,
) {
    if (markSceneAsLoading.match(action)) {
        return state.set(
            action.payload.sceneId,
            ImmutableFromJS<LoadingSceneData>({
                type: action.payload.type,
                context: action.payload.context,
            }),
        );
    }
    if (markSceneAsFinishedLoading.match(action)) {
        return state.delete(action.payload.sceneId);
    }
    return state;
}

export function isStoryboardGenerating(state = false, action: AnyAction) {
    if (setIsStoryboardGenerating.match(action)) {
        return action.payload.isGenerating;
    }
    return state;
}

export function isTextOnMediaGenerating(state = false, action: AnyAction) {
    if (setTextOnMediaGenerating.match(action)) {
        return action.payload.isGenerating;
    }
    return state;
}

export function videoApprovalStatus(
    state: VideoApprovalStatusEnum | null = null,
    action: AnyAction,
) {
    if (setVideoApprovalStatus.match(action)) {
        return action.payload.status;
    }
    return state;
}

export function videoAssignee(state: number | null = null, action: AnyAction) {
    if (setVideoAssignee.match(action)) {
        return action.payload.assignee;
    }
    return state;
}

export function isVideoFeedbackFormVisible(state = false, action: AnyAction) {
    if (setVideoFeedbackFormState.match(action)) {
        return action.payload.isVisible;
    }
    return state;
}

export function videoFeedbackFormSource(state = '', action: AnyAction) {
    if (setVideoFeedbackFormState.match(action)) {
        return action.payload.source ?? '';
    }
    return state;
}

export function windowWidth(state: number | null = null, action: AnyAction) {
    if (setWindowWidth.match(action)) {
        return action.payload.width;
    }
    return state;
}

export function videoInitType(state = VIDEO_INIT_TYPE.CREATE_FROM_SCRATCH, action: AnyAction) {
    if (setVideoInitType.match(action)) {
        return action.payload;
    }
    return state;
}

export function isAdvancedMediaControlsVisible(state = false, action: AnyAction) {
    if (setAdvancedMediaControlsState.match(action) && action.payload.isVisible !== undefined) {
        return action.payload.isVisible;
    }
    if (setChartSettingsState.match(action) && action.payload.isVisible) {
        return false;
    }
    return state;
}

export function advancedMediaControlsSceneId(state = '', action: AnyAction) {
    if (setAdvancedMediaControlsState.match(action)) {
        return action.payload.sceneId;
    }
    return state;
}

export function advancedMediaControlsAssetId(state: string = '', action: AnyAction) {
    if (setAdvancedMediaControlsState.match(action)) {
        return action.payload.assetId;
    }
    return state;
}

export function isChartSettingsVisible(state = false, action: AnyAction) {
    if (setChartSettingsState.match(action) && action.payload.isVisible !== undefined) {
        return action.payload.isVisible;
    }
    if (setAdvancedMediaControlsState.match(action) && action.payload.isVisible) {
        return false;
    }
    return state;
}

export function chartSettingsSceneId(state = '', action: AnyAction) {
    if (setChartSettingsState.match(action)) {
        return action.payload.sceneId;
    }
    return state;
}

export function chartSettingsAssetId(state = '', action: AnyAction) {
    if (setChartSettingsState.match(action)) {
        return action.payload.assetId;
    }
    return state;
}

export function transitionSettings(state = { isVisible: false, sceneId: '' }, action: AnyAction) {
    if (setTransitionSettingsState.match(action)) {
        return action.payload;
    }
    return state;
}

// ID of blueprint selected when the user began video creation wizard
// will be null if user did not start with a blueprint
export function initialBlueprintId(state: number | null = null, action: AnyAction) {
    if (setInitialBlueprintId.match(action)) {
        return action.payload.blueprintId;
    }
    return state;
}

// ID of blueprint actually used to generate content. Can be changed if user selects a different blueprint in SelectBlueprintControl
export function selectedBlueprintId(state: number | null = null, action: AnyAction) {
    if (setBlueprintId.match(action)) {
        return action.payload.blueprintId;
    }
    return state;
}

export function recommendedBlueprint(state: RecommendedBlueprint | null = null, action: AnyAction) {
    if (setRecommendedBlueprint.match(action)) {
        return action.payload.recommendedBlueprint;
    }
    return state;
}

export function asyncGenerationTaskId(state: string | null = null, action: AnyAction) {
    if (setAsyncGenerationTaskId.match(action)) {
        return action.payload.taskId;
    }
    return state;
}

function asyncGenerationData(state: AsyncGenerationData | null = null, action: AnyAction) {
    if (setAsyncGenerationData.match(action)) {
        return action.payload;
    }
    return state;
}

export function skipAgentPipeline(state = false, action: AnyAction) {
    if (setSkipMediaOptimization.match(action)) {
        return action.payload.skip;
    }
    return state;
}

export function dashboardFlow(state: DashboardFlowEnum | null = null, action: AnyAction) {
    if (setDashboardFlow.match(action)) {
        return action.payload.dashboardFlow;
    }
    return state;
}

export function createVideoFlowAnalyticsMethod(
    state: NEW_VIDEO_FLOW_CREATION_METHOD | null = null,
    action: AnyAction,
) {
    if (setCreateVideoFlowAnalyticsMethod.match(action)) {
        return action.payload.createVideoFlowAnalyticsMethod;
    }
    return state;
}

export function createVideoFlowAnalyticsThemeId(
    state: number | string | null = null,
    action: AnyAction,
) {
    if (setCreateVideoFlowAnalyticsThemeId.match(action)) {
        return action.payload.createVideoFlowAnalyticsThemeId;
    }
    return state;
}

export function loadingCreatorActivityId(state: string | null = null, action: AnyAction) {
    if (setLoadingCreatorActivityId.match(action)) {
        return action.payload.loadingCreatorActivityId;
    }
    return state;
}

export function isEditingDraft(state = false, action: AnyAction) {
    if (setIsEditingDraft.match(action)) {
        return action.payload.isEditingDraft;
    }
    return state;
}

export function projectId(state: number | null = null, action: AnyAction) {
    if (setProjectId.match(action)) {
        return action.payload.projectId;
    }
    return state;
}

export function autoCreateSceneGroupFromUploadedAssetUuid(
    state: string | null = null,
    action: AnyAction,
) {
    if (setAutoCreateSceneGroupFromUploadedAssetUuid.match(action)) {
        return action.payload.uuid;
    }
    return state;
}

export function selectedBrandKitId(state: number | null = null, action: AnyAction) {
    if (setSelectedBrandKitId.match(action)) {
        return action.payload.brandKitId;
    }
    return state;
}

export function pendingChart(state: PendingChart | null = null, action: AnyAction) {
    if (setPendingChart.match(action)) {
        return action.payload;
    }
    return state;
}

export const app = combineReducers({
    editor_tab,
    editorTabsVisible,
    selected_aspect_ratio,
    selected_template_id,
    selected_palette_index,
    stage,
    video_language,
    video_id,
    video_slug,
    toolboxCreatingVideo,
    toolboxTurnActive,
    toolboxSidebarLatched,
    scriptComposerPlaceholderVideoId,
    sceneHandoffActive,
    videoDataHash,
    saveConflict,
    sharePageUrl,
    videoVisibility,
    videoName,
    video_project,
    video_owner,
    windowWidth,
    loadingSceneGroups,
    loadingScenes,
    isStoryboardGenerating,
    isTextOnMediaGenerating,
    videoApprovalStatus,
    videoAssignee,
    videoCreationSource,
    isAnimationVideo,
    parentVideoLanguage,
    isVideoFeedbackFormVisible,
    videoFeedbackFormSource,
    videoInitType,
    isAdvancedMediaControlsVisible,
    advancedMediaControlsSceneId,
    advancedMediaControlsAssetId,
    isChartSettingsVisible,
    chartSettingsSceneId,
    chartSettingsAssetId,
    transitionSettings,
    selectedBlueprintId,
    initialBlueprintId,
    recommendedBlueprint,
    asyncGenerationData,
    asyncGenerationTaskId,
    dashboardFlow,
    skipAgentPipeline,
    createVideoFlowAnalyticsMethod,
    createVideoFlowAnalyticsThemeId,
    loadingCreatorActivityId,
    isEditingDraft,
    projectId,
    autoCreateSceneGroupFromUploadedAssetUuid,
    selectedBrandKitId,
    pendingChart,
    multiVideos: multiVideosSlice,
    textAssets: textAssetsReducer,
});

export type AppState = ReturnType<typeof app>;
